import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import Nav from "@/components/Nav";
import PrimeBadge from "@/components/PrimeBadge";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { useTheme } from "@/lib/theme";
import { toast } from "sonner";
import { motion } from "framer-motion";
import { Check, Crown, ArrowRight, ShieldCheck, Zap, HeartHandshake } from "lucide-react";

const ACCENT = "#a855f7";

function usePrimeTokens() {
  const { theme } = useTheme();
  const isLight = theme === "light";
  return {
    isLight,
    pageBg: isLight ? "#f7f7f8" : "#000000",
    text: isLight ? "#0a0a0a" : "#ffffff",
    textMuted: isLight ? "#6a6a70" : "#a0a0a0",
    textDim: isLight ? "#8a8a90" : "#7a7a7a",
    cardBg: isLight ? "#ffffff" : "#111",
    cardBorder: isLight ? "#e5e5ea" : "#2b2b2b",
    softChipBg: isLight ? "#f5f5f7" : "#111",
    softChipBorder: isLight ? "#e5e5ea" : "#2b2b2b",
    manageCardBg: isLight ? "#fafafa" : "#0d0d0d",
    manageCardBorder: isLight ? "#e5e5ea" : "#2b2b2b",
  };
}

export default function Prime() {
  const navigate = useNavigate();
  const { user, revalidate, loading: authLoading } = useAuth();
  const t = usePrimeTokens();
  const [plans, setPlans] = useState([]);
  const [me, setMe] = useState(null);
  const [busy, setBusy] = useState(null); // plan being subscribed to
  const [loading, setLoading] = useState(true);
  const [billingCycle, setBillingCycle] = useState("yearly"); // "monthly" | "yearly"

  useEffect(() => {
    (async () => {
      try {
        const [p, m] = await Promise.all([
          api.get("/prime/plans"),
          user ? api.get("/prime/me") : Promise.resolve({ data: null }),
        ]);
        setPlans(p.data.plans || []);
        setMe(m.data);
        // If the user already has an active plan, default the toggle to their cycle
        if (m.data?.status === "active" && m.data?.billing_cycle) {
          setBillingCycle(m.data.billing_cycle);
        }
      } finally {
        setLoading(false);
      }
    })();
  }, [user]);

  const currentPlan = me?.status === "active" ? me?.plan : null;
  const currentCycle = me?.status === "active" ? me?.billing_cycle : null;

  const subscribe = async (planId) => {
    if (!user) {
      toast.info("Sign in first to activate Pro.");
      navigate("/login", { state: { intent: "prime", plan: planId, billing_cycle: billingCycle } });
      return;
    }
    if (currentPlan === planId && currentCycle === billingCycle) {
      toast.info("You're already on this plan.");
      return;
    }
    setBusy(planId);
    try {
      const { data } = await api.post("/prime/subscribe", { plan: planId, billing_cycle: billingCycle });
      setMe(data);
      await revalidate();
      toast.success(`Welcome to ${planId === "prime_pro" ? "Pro Plus" : "Pro"}!`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to activate membership");
    } finally {
      setBusy(null);
    }
  };

  const cancel = async () => {
    setBusy("cancel");
    try {
      const { data } = await api.post("/prime/cancel");
      setMe(data);
      await revalidate();
      toast.success("Membership cancelled");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to cancel");
    } finally {
      setBusy(null);
    }
  };

  return (
    <div className="min-h-screen" style={{ background: t.pageBg, color: t.text }}>
      <Nav />
      <div className="pt-32 pb-24 px-6 sm:px-10">
        <div className="max-w-6xl mx-auto">
          {/* Hero */}
          <motion.div
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
          >
            <div
              className="inline-flex items-center gap-2 squircle-sm px-3 h-8 text-xs uppercase tracking-widest"
              style={{ background: t.softChipBg, border: `1px solid ${t.softChipBorder}`, color: t.textMuted }}
            >
              Membership
            </div>
            <h1 className="mt-6 text-5xl sm:text-6xl font-bold tracking-tighter max-w-3xl">
              Create Faster.
              <br />
              Save More. <span style={{ color: ACCENT }}>Stay Ahead.</span>
            </h1>
            <p className="mt-5 text-lg max-w-2xl" style={{ color: t.textMuted }}>
              Bliencer Pro is your always-ready creative partner. Priority access,
              automatic discounts, and premium service — built for creators who
              ship every week.
            </p>

            {currentPlan && (
              <div
                className="mt-8 inline-flex items-center gap-3 squircle px-5 py-3 flex-wrap"
                style={{ background: t.cardBg, border: `1px solid ${t.cardBorder}` }}
                data-testid="prime-current-status"
              >
                <PrimeBadge plan={currentPlan} size="md" />
                <span className="text-sm" style={{ color: t.textMuted }}>
                  Active · {currentCycle === "yearly" ? "billed annually" : "billed monthly"} · renews {new Date(me.renewal_date).toLocaleDateString()}
                </span>
                <span className="text-sm">
                  Lifetime savings <span className="font-semibold" style={{ color: ACCENT }}>${(me.lifetime_savings || 0).toFixed(2)}</span>
                </span>
              </div>
            )}
          </motion.div>

          {/* Billing cycle toggle */}
          <div className="mt-14 flex justify-center">
            <BillingToggle billingCycle={billingCycle} setBillingCycle={setBillingCycle} t={t} />
          </div>

          {/* Plan cards */}
          <div className="mt-6 grid md:grid-cols-2 gap-5">
            {plans.map((p) => (
              <PlanCard
                key={p.id}
                plan={p}
                currentPlan={currentPlan}
                currentCycle={currentCycle}
                billingCycle={billingCycle}
                busy={busy === p.id}
                onSubscribe={() => subscribe(p.id)}
                highlight={p.id === "prime_pro"}
                t={t}
              />
            ))}
          </div>

          {/* Value pillars */}
          <div className="mt-20">
            <div className="text-[11px] uppercase tracking-widest" style={{ color: t.textMuted }}>
              How it saves you time & money
            </div>
            <h2 className="mt-2 text-3xl sm:text-4xl font-bold tracking-tighter">
              Built like Prime. Tuned for creators.
            </h2>

            <div className="mt-8 grid sm:grid-cols-3 gap-4">
              <Pillar Icon={Zap} title="Priority queue" body="Your projects skip the line. Pro Plus members get top placement across the studio." t={t} />
              <Pillar Icon={ShieldCheck} title="Automatic discounts" body="15% off for Pro, 25% off for Pro Plus. Applied at checkout — no coupons." t={t} />
              <Pillar Icon={HeartHandshake} title="Dedicated support" body="Faster replies, unlimited revisions, and monthly strategy calls on Pro Plus." t={t} />
            </div>
          </div>

          {/* Cancel */}
          {currentPlan && (
            <div
              className="mt-16 squircle p-6 flex flex-wrap items-center justify-between gap-4"
              style={{ background: t.manageCardBg, border: `1px solid ${t.manageCardBorder}` }}
            >
              <div>
                <div className="text-sm font-medium">Manage your membership</div>
                <div className="text-xs text-[#a0a0a0] mt-1">
                  Cancel anytime. You'll keep member benefits until{" "}
                  {new Date(me.renewal_date).toLocaleDateString()}.
                </div>
              </div>
              <button
                onClick={cancel}
                disabled={busy === "cancel"}
                data-testid="prime-cancel-btn"
                className="squircle-sm border border-[#2b2b2b] px-5 h-10 text-sm hover:bg-[#111] btn-anim disabled:opacity-50"
              >
                {busy === "cancel" ? "Cancelling…" : "Cancel membership"}
              </button>
            </div>
          )}

          <p className="mt-16 text-xs text-[#7a7a7a] text-center">
            Prime billing is currently in preview mode — no charges will be made
            until Stripe is enabled.
          </p>
        </div>
      </div>
    </div>
  );
}

function PlanCard({ plan, currentPlan, currentCycle, billingCycle, busy, onSubscribe, highlight, t }) {
  const isYearly = billingCycle === "yearly";
  const isCurrent = currentPlan === plan.id && currentCycle === billingCycle;
  const canSubscribe = !isCurrent;
  const label = isCurrent ? "Current plan" : `Get ${plan.name}`;
  const shortName = plan.id === "prime_pro" ? "Pro Plus" : "Pro";
  const discountPct = Math.round((plan.discount || 0) * 100);
  // Yearly display: monthly-equivalent from backend + strikethrough of monthly price.
  const yearlyMonthly = plan.yearly_price_per_month || Math.round(plan.price * 0.8);
  const displayPrice = isYearly ? yearlyMonthly : plan.price;
  const yearlyTotal = plan.yearly_price || Math.round(plan.price * 12 * 0.8);
  const yearlySavings = Math.max(0, Math.round(plan.price * 12 - yearlyTotal));

  return (
    <motion.div
      initial={{ opacity: 0, y: 12 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.4 }}
      className="squircle p-8 relative overflow-hidden"
      style={{
        background: highlight
          ? t.isLight
            ? "linear-gradient(180deg, #f7f2ff 0%, #ffffff 55%)"
            : "linear-gradient(180deg, rgba(88,28,135,0.28) 0%, rgba(30,10,50,0.55) 55%, #0b0714 100%)"
          : t.cardBg,
        border: `1px solid ${highlight ? (t.isLight ? "#e4d4ff" : "rgba(168,85,247,0.32)") : t.cardBorder}`,
        boxShadow: highlight
          ? t.isLight
            ? "0 24px 60px -30px rgba(168,85,247,0.35)"
            : "inset 0 1px 0 rgba(255,255,255,0.05), 0 24px 60px -30px rgba(168,85,247,0.55)"
          : "none",
        color: t.text,
      }}
      data-testid={`plan-card-${plan.id}`}
    >
      {/* Subtle purple ambient glow — only for Pro Plus */}
      {highlight && (
        <div
          aria-hidden
          className="absolute inset-0 pointer-events-none"
          style={{
            background:
              "radial-gradient(600px 220px at 100% 0%, rgba(168,85,247,0.22), transparent 60%)",
          }}
        />
      )}

      {/* Header — label + inline discount pill (Higgsfield style) */}
      <div className="relative flex items-center gap-3">
        <span
          className="text-[26px] sm:text-[28px] font-black tracking-[0.02em] uppercase leading-none"
          style={{
            color: t.text,
            letterSpacing: "-0.005em",
          }}
        >
          {shortName}
        </span>
        {discountPct > 0 && (
          <span
            className="inline-flex items-center h-6 px-2 rounded-md text-[10.5px] font-bold uppercase tracking-widest"
            style={{
              background: "linear-gradient(120deg, #c084fc 0%, #a855f7 50%, #8b5cf6 100%)",
              color: "#1a0d2e",
              boxShadow:
                "0 0 0 1px rgba(255,255,255,0.22) inset, 0 2px 10px -4px rgba(168,85,247,0.36)",
            }}
          >
            {discountPct}% OFF
          </span>
        )}
        {isCurrent && (
          <span
            className="inline-flex items-center gap-1 h-5 px-2 rounded-md text-[10px] uppercase tracking-widest font-semibold"
            style={{ background: "rgba(34,197,94,0.15)", color: "#16a34a", border: "1px solid rgba(34,197,94,0.4)" }}
          >
            Active
          </span>
        )}
      </div>

      {/* Positioning subtitle */}
      <div className="relative mt-2.5 text-[14px]" style={{ color: t.textMuted }}>
        {plan.positioning}
      </div>

      {/* Included / stat box — Higgsfield-inspired credits panel */}
      <div
        className="relative mt-6 rounded-2xl p-5"
        style={{
          background: highlight
            ? t.isLight ? "rgba(168,85,247,0.06)" : "rgba(168,85,247,0.08)"
            : t.isLight ? "#f5f5f7" : "#0d0d0d",
          border: `1px solid ${
            highlight
              ? t.isLight ? "rgba(168,85,247,0.22)" : "rgba(168,85,247,0.22)"
              : t.cardBorder
          }`,
        }}
      >
        <div className="flex items-center gap-2">
          <span style={{ color: ACCENT, filter: "drop-shadow(0 0 6px rgba(168,85,247,0.7))" }}>✦</span>
          <div className="text-[20px] font-bold tracking-tight" style={{ color: t.text }}>
            {plan.id === "prime_pro" ? "VIP queue placement" : "Priority queue placement"}
          </div>
        </div>
        <div className="mt-1.5 text-[13px]" style={{ color: t.textMuted }}>
          = ETA shortened by {plan.eta_days_reduction || 1} day{(plan.eta_days_reduction || 1) > 1 ? "s" : ""} per project
        </div>
        <div className="mt-1 text-[13px]" style={{ color: t.textMuted }}>
          ~ {discountPct}% saved on every service order
        </div>
        <div
          className="mt-4 flex items-center gap-2 rounded-xl px-3 py-2 text-[12.5px]"
          style={{
            background: t.isLight ? "#ffffff" : "rgba(255,255,255,0.03)",
            border: `1px solid ${t.isLight ? "#ececf1" : "rgba(255,255,255,0.06)"}`,
            color: t.textMuted,
          }}
        >
          <Check size={13} strokeWidth={2.5} color={ACCENT} />
          {plan.id === "prime_pro"
            ? "Includes 1 free thumbnail credit / billing cycle"
            : "Automatic checkout discount — no coupon needed"}
        </div>
      </div>

      {/* Price */}
      <div className="relative mt-7 flex items-baseline gap-2.5 flex-wrap" style={{ color: t.text }}>
        {isYearly && (
          <span
            className="text-2xl sm:text-3xl font-bold tracking-tighter line-through"
            style={{ color: t.isLight ? "#c7c7cf" : "#5a5a63", textDecorationThickness: "2px" }}
          >
            ${plan.price}
          </span>
        )}
        <span className="text-5xl font-black tracking-tighter" style={{ letterSpacing: "-0.03em" }}>
          ${displayPrice}
        </span>
        <span className="text-[13px]" style={{ color: t.textMuted }}>
          per month{isYearly ? ", billed annually" : ""}
        </span>
      </div>
      {isYearly && (
        <div className="mt-1.5 text-[12px]" style={{ color: t.textMuted }}>
          ${yearlyTotal} billed once yearly
        </div>
      )}

      {/* CTA */}
      <button
        onClick={onSubscribe}
        disabled={busy || isCurrent}
        data-testid={`subscribe-${plan.id}`}
        className="prime-cta mt-6 w-full squircle-sm px-5 h-12 text-sm font-semibold btn-anim inline-flex items-center justify-center gap-2 disabled:opacity-60 disabled:cursor-not-allowed"
        style={{
          background: "linear-gradient(120deg, #c084fc 0%, #a855f7 45%, #8b5cf6 100%)",
          color: "#1a0d2e",
          boxShadow:
            "0 0 0 1px rgba(255,255,255,0.22) inset, 0 2px 10px -4px rgba(168,85,247,0.32), 0 0 14px -6px rgba(139,92,246,0.28)",
          letterSpacing: "-0.005em",
        }}
      >
        {busy ? "Activating…" : label}
        {canSubscribe && !busy && <ArrowRight size={14} strokeWidth={2.5} />}
      </button>

      {/* Savings microcopy — reference-style */}
      <div
        className="mt-3 rounded-xl px-4 py-2.5 text-center text-[12.5px]"
        style={{
          background: t.isLight ? "#f5f5f7" : "rgba(255,255,255,0.03)",
          border: `1px solid ${t.isLight ? "#ececf1" : "rgba(255,255,255,0.06)"}`,
          color: t.textMuted,
        }}
      >
        {isYearly ? (
          <>
            <span className="font-semibold" style={{ color: t.text }}>Save ${yearlySavings}/year</span> compared to monthly
          </>
        ) : (
          <>Cancel anytime · no long-term commitment</>
        )}
      </div>

      {/* Benefits list — reference style: underlined accent items */}
      <ul className="relative mt-6 space-y-3">
        {plan.benefits.map((b, i) => {
          const emphasize = i < 3; // first three benefits highlighted with purple underline
          return (
            <li key={b.name} className="flex items-start gap-2.5">
              <Check
                size={14}
                strokeWidth={2.5}
                color={emphasize ? ACCENT : t.textMuted}
                className="flex-shrink-0 mt-[3px]"
                style={emphasize ? { filter: "drop-shadow(0 0 4px rgba(168,85,247,0.55))" } : {}}
              />
              <span
                className="text-[13.5px] leading-snug"
                style={{
                  color: t.text,
                  textDecoration: emphasize ? "underline" : "none",
                  textDecorationColor: emphasize ? "rgba(168,85,247,0.55)" : "transparent",
                  textUnderlineOffset: "3px",
                  textDecorationThickness: "1.5px",
                }}
              >
                {b.name}
              </span>
            </li>
          );
        })}
      </ul>

      {/* Footer feature chip — reference-style bottom rail */}
      <div
        className="relative mt-6 rounded-2xl p-4"
        style={{
          background: highlight
            ? t.isLight ? "rgba(168,85,247,0.08)" : "rgba(168,85,247,0.10)"
            : t.isLight ? "#f5f5f7" : "#0d0d0d",
          border: `1px solid ${
            highlight
              ? "rgba(168,85,247,0.28)"
              : t.cardBorder
          }`,
        }}
      >
        <div className="flex items-center gap-2">
          <Crown size={13} color={ACCENT} style={{ filter: "drop-shadow(0 0 6px rgba(168,85,247,0.6))" }} />
          <div className="text-[11px] font-bold uppercase tracking-[0.18em]" style={{ color: t.text }}>
            {plan.id === "prime_pro" ? "VIP tier · full studio access" : "Member tier · priority studio"}
          </div>
          {highlight && (
            <span
              className="ml-auto inline-flex items-center h-5 px-2 rounded-full text-[9.5px] font-bold uppercase tracking-wider"
              style={{
                background: "linear-gradient(120deg, #c084fc 0%, #a855f7 50%, #8b5cf6 100%)",
                color: "#1a0d2e",
                boxShadow: "0 0 0 1px rgba(255,255,255,0.28) inset",
              }}
            >
              Full power
            </span>
          )}
        </div>
        <div className="mt-2 text-[12px]" style={{ color: t.textMuted }}>
          {plan.id === "prime_pro"
            ? "Every service, every style, every deliverable — placed at the top of the studio queue with a dedicated line to your producer."
            : "Selected services delivered on an expedited track. Upgrade to Pro Plus for VIP access across the whole studio."}
        </div>
      </div>
    </motion.div>
  );
}

function Pillar({ Icon, title, body, t }) {
  return (
    <div className="squircle p-6" style={{ background: t.cardBg, border: `1px solid ${t.cardBorder}` }}>
      <Icon
        size={30}
        strokeWidth={1.8}
        color={ACCENT}
        style={{ filter: "drop-shadow(0 0 10px rgba(168,85,247,0.55)) drop-shadow(0 0 22px rgba(168,85,247,0.25))" }}
      />
      <div className="mt-5 font-semibold" style={{ color: t.text }}>{title}</div>
      <div className="mt-1 text-sm" style={{ color: t.textMuted }}>{body}</div>
    </div>
  );
}

function BillingToggle({ billingCycle, setBillingCycle, t }) {
  const isYearly = billingCycle === "yearly";
  const baseBtn = "relative z-10 inline-flex items-center justify-center gap-1.5 px-3.5 sm:px-4 h-7 rounded-full text-[11.5px] font-semibold btn-anim whitespace-nowrap";
  const activeGradient = "linear-gradient(120deg, #c084fc 0%, #a855f7 50%, #8b5cf6 100%)";
  const activeShadow = "0 0 0 1px rgba(255,255,255,0.22) inset, 0 1px 6px -2px rgba(168,85,247,0.32)";

  return (
    <div
      role="tablist"
      aria-label="Billing cycle"
      className="inline-flex items-center gap-0.5 p-0.5 rounded-full"
      style={{
        background: t.isLight ? "#ececf1" : "rgba(255,255,255,0.05)",
        border: `1px solid ${t.isLight ? "#e5e5ea" : "rgba(255,255,255,0.08)"}`,
      }}
      data-testid="billing-toggle"
    >
      <button
        role="tab"
        aria-selected={!isYearly}
        onClick={() => setBillingCycle("monthly")}
        data-testid="billing-toggle-monthly"
        className={baseBtn}
        style={{
          background: !isYearly ? (t.isLight ? "#ffffff" : "#1a1a1a") : "transparent",
          color: !isYearly ? (t.isLight ? "#0a0a0a" : "#ffffff") : t.textMuted,
          boxShadow: !isYearly ? "0 1px 2px rgba(0,0,0,0.12)" : "none",
        }}
      >
        Monthly
      </button>
      <button
        role="tab"
        aria-selected={isYearly}
        onClick={() => setBillingCycle("yearly")}
        data-testid="billing-toggle-yearly"
        className={baseBtn}
        style={{
          background: isYearly ? activeGradient : "transparent",
          color: isYearly ? "#1a0d2e" : t.text,
          boxShadow: isYearly ? activeShadow : "none",
        }}
      >
        Yearly
        <span
          className="inline-flex items-center h-4 px-1.5 rounded-full text-[9px] font-bold uppercase tracking-wider"
          style={{
            background: isYearly ? "rgba(26,13,46,0.16)" : "rgba(168,85,247,0.18)",
            color: isYearly ? "#1a0d2e" : "#c084fc",
            border: isYearly ? "1px solid rgba(26,13,46,0.24)" : "1px solid rgba(168,85,247,0.35)",
          }}
        >
          Save 20%
        </span>
      </button>
    </div>
  );
}

